import { useState } from "react";
import { ExpandableCard } from "../primitives/expandable-card";
import { ProgressChart } from "./progress-chart";
import type { MeasurementPoint } from "../../lib/dashboard/data";

interface MeasurementCardProps {
  title: string;
  /** Already formatted by metric-format.ts, number only: "23.7", "97". */
  value: string;
  unit: string;
  /** Already formatted with its unit: "−3.0 pts", "−5 cm". Omitted when zero or unknown. */
  change?: string;
  /** "26 Mar" when the value is from an earlier scan than the client's latest. */
  asOf?: string;
  lastUpdate?: string;
  testId?: string;
  /** Band label for this reading, e.g. "Healthy". Body fat only. */
  category?: string;
  isTarget?: boolean;
  myTarget?: number;
  setTarget?: (value: number) => void;
  values?: MeasurementPoint[];
  onTargetChange?: (value: number, commit:boolean) => void;
}

const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-GB", {
      month: "numeric",
      year: "2-digit",
      day: "numeric",
    });
  };

export const MeasurementCard = ({
    title,
    value,
    unit,
    change,
    asOf,
    lastUpdate,
    testId,
    category,
    values,
    isTarget,
    myTarget,
    setTarget,
    onTargetChange,
  }: MeasurementCardProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const isExpandible = values && values.length > 1;
    const isBodyFat = title.toLowerCase().includes("body");

    const toDataPoints = (values?: MeasurementPoint[]) => {
        if (!values) return [];

        return values.map((point) => ({
            date: formatDate(point.date),
            value: point.value,
        }));
    };

    return (
      <ExpandableCard
        testId={testId}
        title={isTarget ? undefined : title}
        canExpand={!!isExpandible}
        isExpanded={isExpanded}
        onToggle={() => setIsExpanded(!isExpanded)}
        chevronClassName="text-muted-foreground"
        headerClassName="cursor-pointer"
        expandedContent={
          <ProgressChart
            data={toDataPoints(values)}
            myTarget={isBodyFat ? myTarget : undefined}
            title={`${title} History`}
            unit={unit}
          />
        }
      >
        {/* Body fat used to render a four-band red-amber-green strip here.
            The concept drops the traffic light, and the accessibility work
            explains why it cannot be rescued: no four-band palette passes,
            because green-to-amber adjacency *is* the protan confusion axis,
            and four hues inside the required lightness band leave nowhere to
            escape to. The finding of that work was that the *labels* are what
            carry the meaning. So the label stays and the colour goes — the
            clinical reading is not lost, only the encoding that could not be
            made legible. */}
        {(
          <div className="flex items-end justify-between">
            <div className="flex items-baseline space-x-2">
              <span className="tabular text-2xl font-semibold">{value}</span>
              <span className="text-sm text-muted-foreground">{unit}</span>
              {/* Carried from an earlier scan, so say which: a weight from
                  March read as today's beside a Now column that had none. */}
              {asOf && (
                <span data-testid="measurement-as-of" className="tabular text-xs text-muted-foreground">
                  · {asOf}
                </span>
              )}
            </div>
            {/* One neutral treatment, no red and no green.

                This used to be `isPositive ? text-green-500 : text-red-500`,
                which reads every rise as good and every fall as alarming. A
                waist down 4 cm is the thing the client is paying for, and it
                rendered in the same red as a failure; body fat did too. Muscle
                is the only tracked measurement where that rule holds.

                A per-metric direction map would not fix it either: whether a
                thigh going up is progress depends on the goal, and the concept
                answers this explicitly — the same four measurements for every
                goal, with only the highlighted delta changing. So the sign
                carries the information and the colour carries none, which is
                what every other delta in the concept does. */}
            {change && (
              <span data-testid="measurement-change" className="tabular text-sm font-semibold text-primary">
                {change}
                <span className="ml-1 font-normal text-muted-foreground">
                  since start
                </span>
              </span>
            )}
          </div>
        )}

        {isBodyFat && (category || myTarget !== undefined) && (
          <div className="tabular mt-1 flex items-baseline gap-3 text-[11px]">
            {category && <span className="text-foreground-soft">{category}</span>}
            {myTarget !== undefined && (
              <span className="text-[hsl(var(--projected))]">Target {myTarget} %</span>
            )}
          </div>
        )}

        {lastUpdate && (
          <p className="text-xs text-muted-foreground">{`Updated ${lastUpdate}`}</p>
        )}
      </ExpandableCard>
    );
  };
